import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Clock,
  CheckCircle2,
  Edit,
  XCircle,
  User,
  Calendar,
  Package,
  Hash,
  FileText,
  ArrowRightLeft,
  MapPin,
  PlayCircle,
  Trash2,
  Send,
  Copy,
} from "lucide-react";
import { StockTransfer } from "@/pages/stock-transfers";

interface StockTransferDrawerDetailsProps {
  transfer: StockTransfer;
  transferItems?: Array<{
    itemCode: string;
    itemName: string;
    sourceLocation: string;
    destinationLocation: string;
    quantity: number;
  }>;
  onEdit?: () => void;
  onDelete?: () => void;
  onContinue?: () => void;
  onShipTransfer?: () => void;
  onDuplicate?: () => void;
  onReceive?: () => void;
}

const getStatusColor = (status: StockTransfer["status"]) => {
  switch (status) {
    case "draft":
      return "bg-gray-500/10 text-gray-500 border-gray-500/20";
    case "in-transit":
      return "bg-blue-500/10 text-blue-500 border-blue-500/20";
    case "completed":
      return "bg-green-500/10 text-green-500 border-green-500/20";
    case "cancelled":
      return "bg-red-500/10 text-red-500 border-red-500/20";
    default:
      return "bg-gray-500/10 text-gray-500 border-gray-500/20";
  }
};

const getStatusIcon = (status: StockTransfer["status"]) => {
  switch (status) {
    case "draft":
      return <Edit className="h-3 w-3" />;
    case "in-transit":
      return <ArrowRightLeft className="h-3 w-3" />;
    case "completed":
      return <CheckCircle2 className="h-3 w-3" />;
    case "cancelled":
      return <XCircle className="h-3 w-3" />;
    default:
      return null;
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatStatus = (status: StockTransfer["status"]) => {
  switch (status) {
    case "in-transit":
      return "In Transit";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

export function StockTransferDrawerDetails({
  transfer,
  transferItems = [],
  onEdit,
  onDelete,
  onContinue,
  onShipTransfer,
  onDuplicate,
  onReceive,
}: StockTransferDrawerDetailsProps) {
  const canEdit = transfer.status === "draft";
  const canDelete = transfer.status === "draft";
  const canReceive = transfer.status === "in-transit";

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      {canEdit && (
        <div className="space-y-2">
          <div className="flex gap-2">
            {transfer.status === "draft" && onContinue && (
              <Button onClick={onContinue} variant="outline" className="flex-1">
                <PlayCircle className="h-4 w-4 mr-2" />
                Continue Editing
              </Button>
            )}
            {transfer.status === "draft" && onShipTransfer && (
              <Button onClick={onShipTransfer} className="flex-1">
                <Send className="h-4 w-4 mr-2" />
                Ship Transfer
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {onDuplicate && (
              <Button onClick={onDuplicate} variant="outline" className="flex-1">
                <Copy className="h-4 w-4 mr-2" />
                Duplicate Transfer
              </Button>
            )}
            {canDelete && onDelete && (
              <Button onClick={onDelete} variant="destructive" className="flex-1">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Draft
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Action Buttons for in-transit transfers */}
      {canReceive && onReceive && (
        <div className="space-y-2">
          <Button onClick={onReceive} className="w-full">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Mark as Received
          </Button>
          {onDuplicate && (
            <Button onClick={onDuplicate} variant="outline" className="w-full">
              <Copy className="h-4 w-4 mr-2" />
              Duplicate Transfer
            </Button>
          )}
        </div>
      )}
      
      {/* Action Buttons for completed/cancelled transfers */}
      {!canEdit && !canReceive && onDuplicate && (
        <div className="space-y-2">
          <Button onClick={onDuplicate} variant="outline" className="w-full">
            <Copy className="h-4 w-4 mr-2" />
            Duplicate Transfer
          </Button>
        </div>
      )}

      {/* Transfer Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Transfer Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Reference
              </div>
              <div className="font-medium">{transfer.reference}</div>
            </div>

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Status</div>
              <Badge variant="outline" className={getStatusColor(transfer.status)}>
                <span className="flex items-center gap-1">
                  {getStatusIcon(transfer.status)}
                  {formatStatus(transfer.status)}
                </span>
              </Badge>
            </div>

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <User className="h-4 w-4" />
                Created By
              </div>
              <div className="font-medium">{transfer.createdBy}</div>
            </div>

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Created At
              </div>
              <div className="font-medium">{formatDate(transfer.createdAt)}</div>
            </div>

            {transfer.dispatchedAt && (
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" />
                  Dispatched At
                </div>
                <div className="font-medium">{formatDate(transfer.dispatchedAt)}</div>
              </div>
            )}

            {transfer.expectedArrivalDate && (
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Expected Arrival
                </div>
                <div className="font-medium">
                  {new Date(transfer.expectedArrivalDate).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>
            )}

            {transfer.completedAt && (
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Completed At
                </div>
                <div className="font-medium">{formatDate(transfer.completedAt)}</div>
              </div>
            )}

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Package className="h-4 w-4" />
                Total Items
              </div>
              <div className="font-medium">{transfer.itemCount}</div>
            </div>

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4" />
                Total Quantity
              </div>
              <div className="font-medium">{transfer.totalQuantity}</div>
            </div>
          </div>

          {transfer.notes && (
            <>
              <Separator />
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Notes</div>
                <div className="text-sm bg-muted p-3 rounded-md">{transfer.notes}</div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Transfer Items */}
      {transferItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Transfer Items
            </CardTitle>
            <CardDescription>
              {transferItems.length} item{transferItems.length !== 1 ? "s" : ""} in this transfer
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item Code</TableHead>
                    <TableHead>Item Name</TableHead>
                    <TableHead>From Location</TableHead>
                    <TableHead>To Location</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transferItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.itemCode}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {item.sourceLocation}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {item.destinationLocation}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{item.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Placeholder for when no items are available */}
      {transferItems.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Transfer Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No item details available for this transfer</p>
              <p className="text-sm mt-1">
                Item details are stored with transfers created after this feature was added
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
